// DefectTaskLinksCard — "Verknüpfte PT-TSK-Tasks" in der Mangel-Detailansicht (F-CHK-TSK, TES-611/616).
// Zeigt die realen defect_task_links eines Mangels (pt_task_id + gespiegelter Status), ein Banner solange
// offene Tasks den Mangel sperren (status_locked-Invariante, Migr. 304), ein Modal zum Anlegen einer
// neuen Verknüpfung (Edge Function defect-task-link, user-JWT → RLS, TES-611) und das Entfernen einer
// Verknüpfung mit Bestätigungs-Dialog (direkter RLS-DELETE, TES-616).

import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link2, ListPlus, Lock, ClipboardList, Trash2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { NoAssignmentConfirmDialog } from "@/components/defects/NoAssignmentConfirmDialog";
import {
  useDefectTaskLinks,
  useLinkTaskToDefect,
  useUnlinkTaskFromDefect,
  openTaskLinkCount,
  type DefectTaskLink,
} from "@/hooks/useDefectTaskLinks";
import { TaskGroupProgress } from "@/components/tasks/TaskGroupProgress";

/** Externe PT-TSK-Task-Referenz — deckungsgleich mit contracts/pt-tsk.yaml + defect-task-link/logic.ts. */
const PT_TASK_ID_RE = /^[A-Za-z0-9][A-Za-z0-9._-]{2,63}$/;

type BadgeVariant = "default" | "secondary" | "outline" | "destructive";

/** Badge-Tonalität je gespiegeltem PT-TSK-Status (done/completed = erledigt, offen = neutral). */
function taskStatusVariant(status: string | null): BadgeVariant {
  if (status === "done" || status === "completed") return "secondary";
  if (status === "cancelled") return "outline";
  return "default";
}

export function DefectTaskLinksCard({ defectId }: { defectId: string }) {
  const { t } = useTranslation();
  const { data: links, isLoading } = useDefectTaskLinks(defectId);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmLink, setConfirmLink] = useState<DefectTaskLink | null>(null);
  const unlink = useUnlinkTaskFromDefect(defectId);

  const rows = useMemo(() => links ?? [], [links]);
  const openCount = useMemo(() => openTaskLinkCount(rows), [rows]);

  const confirmUnlink = () => {
    if (!confirmLink) return;
    unlink.mutate(confirmLink.id, { onSettled: () => setConfirmLink(null) });
  };

  return (
    <section className="bg-card border border-outline-variant rounded-lg">
      <div className="p-4 border-b border-outline-variant flex items-center justify-between gap-2">
        <div className="flex items-center gap-2 min-w-0">
          <Link2 className="h-5 w-5 text-primary shrink-0" aria-hidden="true" />
          <h3 className="font-display text-lg font-semibold truncate">
            {t("defects.taskLinks.title")}
          </h3>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setDialogOpen(true)}
          data-testid="task-link-add-button"
        >
          <ListPlus className="h-4 w-4 mr-1" aria-hidden="true" />
          {t("defects.taskLinks.add")}
        </Button>
      </div>

      {/* TES-1961: config-getriebenes Status/Fortschritt-Widget der verknüpften PT-TSK-Task-Gruppe. */}
      <TaskGroupProgress links={rows} isLoading={isLoading} />

      {openCount > 0 && <StatusLockBanner count={openCount} />}

      <TaskLinkList rows={rows} isLoading={isLoading} onUnlink={setConfirmLink} />

      <LinkTaskDialog defectId={defectId} open={dialogOpen} onOpenChange={setDialogOpen} />
      <NoAssignmentConfirmDialog
        open={confirmLink !== null}
        onOpenChange={(open) => !open && setConfirmLink(null)}
        title={t("defects.taskLinks.unlinkConfirmTitle")}
        description={t("defects.taskLinks.unlinkConfirmBody", {
          taskId: confirmLink?.pt_task_id ?? "",
        })}
        cancelLabel={t("common.cancel")}
        confirmLabel={t("defects.taskLinks.unlinkButton")}
        onConfirm={confirmUnlink}
      />
    </section>
  );
}

/** Banner: solange offene Tasks bestehen, kann der Mangel nicht geschlossen werden. */
function StatusLockBanner({ count }: { count: number }) {
  const { t } = useTranslation();
  return (
    <div
      role="alert"
      data-testid="task-link-lock-banner"
      className="m-4 flex items-start gap-2 rounded border border-warning/40 bg-warning/10 p-3 text-sm text-warning-foreground"
    >
      <Lock className="h-4 w-4 mt-0.5 shrink-0 text-warning" aria-hidden="true" />
      <span>{t("defects.taskLinks.lockedBanner", { count })}</span>
    </div>
  );
}

/** Empty-State oder Tabelle der Task-Links. */
function TaskLinkList({
  rows,
  isLoading,
  onUnlink,
}: {
  rows: DefectTaskLink[];
  isLoading: boolean;
  onUnlink: (link: DefectTaskLink) => void;
}) {
  const { t } = useTranslation();

  if (!isLoading && rows.length === 0) {
    return (
      <div className="p-6 flex flex-col items-center justify-center text-center gap-2 text-muted-foreground">
        <ClipboardList className="h-6 w-6" aria-hidden="true" />
        <p className="text-sm">{t("defects.taskLinks.empty")}</p>
      </div>
    );
  }

  return (
    <table className="w-full text-left">
      <thead className="bg-surface-container-low text-label-caps text-muted-foreground">
        <tr>
          <th className="px-4 py-3 border-b border-outline-variant font-bold">
            {t("defects.taskLinks.taskId")}
          </th>
          <th className="px-4 py-3 border-b border-outline-variant font-bold">
            {t("defects.taskLinks.taskStatus")}
          </th>
          <th className="px-4 py-3 border-b border-outline-variant">
            <span className="sr-only">{t("defects.taskLinks.actions")}</span>
          </th>
        </tr>
      </thead>
      <tbody className="text-data-tabular" data-testid="task-link-rows">
        {rows.map((link) => (
          <TaskLinkRow key={link.id} link={link} onUnlink={onUnlink} />
        ))}
      </tbody>
    </table>
  );
}

/** Eine Link-Zeile (pt_task_id + Status-Badge + Entfernen-Button). */
function TaskLinkRow({
  link,
  onUnlink,
}: {
  link: DefectTaskLink;
  onUnlink: (link: DefectTaskLink) => void;
}) {
  const { t } = useTranslation();
  const status = link.pt_task_status;
  const statusLabel = status ? t(`defects.taskLinks.status.${status}`, status) : "—";
  return (
    <tr className="hover:bg-surface-container-low transition-colors">
      <td className="px-4 py-3 border-b border-outline-variant font-mono break-all">
        {link.pt_task_id}
      </td>
      <td className="px-4 py-3 border-b border-outline-variant">
        <Badge variant={taskStatusVariant(status)}>{statusLabel}</Badge>
      </td>
      <td className="px-4 py-3 border-b border-outline-variant text-right">
        <Button
          size="sm"
          variant="ghost"
          onClick={() => onUnlink(link)}
          aria-label={t("defects.taskLinks.unlinkButton")}
          data-testid={`task-link-unlink-${link.id}`}
        >
          <Trash2 className="h-4 w-4" aria-hidden="true" />
        </Button>
      </td>
    </tr>
  );
}

/** Modal zum Anlegen einer Verknüpfung (Text-Input für pt_task_id, clientseitige Format-Validierung). */
function LinkTaskDialog({
  defectId,
  open,
  onOpenChange,
}: {
  defectId: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}) {
  const { t } = useTranslation();
  const [value, setValue] = useState("");
  const linkTask = useLinkTaskToDefect(defectId);

  const trimmed = value.trim();
  const isValid = PT_TASK_ID_RE.test(trimmed);

  const submit = () => {
    if (!isValid) return;
    linkTask.mutate(trimmed, {
      onSuccess: () => {
        setValue("");
        onOpenChange(false);
      },
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("defects.taskLinks.add")}</DialogTitle>
          <DialogDescription>{t("defects.taskLinks.addHint")}</DialogDescription>
        </DialogHeader>
        <div className="space-y-2">
          <Label htmlFor="pt-task-id">{t("defects.taskLinks.taskId")}</Label>
          <Input
            id="pt-task-id"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            placeholder="PT-TSK-1003"
            autoComplete="off"
            data-testid="task-link-input"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button
            onClick={submit}
            disabled={!isValid || linkTask.isPending}
            data-testid="task-link-submit"
          >
            {t("defects.taskLinks.linkButton")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
